import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, IconButton, Dialog, DialogContent,
    DialogContentText, Snackbar, Alert, Input
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BorderColor from '@mui/icons-material/BorderColor';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import * as common from '../../commons/common';
import CheckOnIcon from '../../images/icon_check_on.png';
import CheckOffIcon from '../../images/icon_check_off.png';

export default function ComplaintInput() {
    const navigate = useNavigate();
    const location = useLocation();
    const { facilityId } = useParams();

    const [open, setOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [facilityName, setFacilityName] = useState('');
    const [facilityTypeId, setFacilityTypeId] = useState('');
    const [group, setGroup] = useState('');
    const [complaintDatas, setComplaintDatas] = useState([]); //민원항목 리스트
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [summitLoading, setSummitLoading] = useState(false);

    useEffect(() => {
        setSummitLoading(false)
        window.history.pushState(null, document.title, window.location.href);
        window.addEventListener('popstate', handlePopState);
        window.addEventListener('beforeunload', handleBeforeUnload);

        // qr 데이터 가져오기
        const fetchData = async () => {
            const url = `${common.getApiUrl()}/qr/${facilityId}`;
            try {
                const response = await axios.get(url, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data) {
                    setFacilityName(response.data.facilityNm);
                    setFacilityTypeId(response.data.facilityTypeId);
                    setGroup(response.data.groupNm);
                    const url = `${common.getApiUrl()}/qr/facility-type/complaint-item`;
                    const facilityTypeId = response.data.facilityTypeId
                    try {
                        const response = await axios.get(url, {
                            params: { facilityTypeId },
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        });
                        if (response.data) {
                            setComplaintDatas(response.data.map(item => item.complaintItemNm));
                        }
                    } catch (error) {
                        common.handleApiError(error);
                    }
                }
            } catch (error) {
                common.handleApiError(error);
            }
        };

        fetchData();

        // URL에서 msg 매개변수 추출
        const queryParams = new URLSearchParams(location.search);
        const msg = queryParams.get('msg');
        if (msg) {
            setOpen(true);
        }

        return () => {
            window.removeEventListener('popstate', handlePopState);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [open, location, facilityId]);

    const handlePopState = (event) => {
        window.history.pushState(null, document.title, window.location.href);
    };

    const handleBeforeUnload = (event) => {
        if (open) {
            event.preventDefault();
            event.returnValue = '';
            return '';
        }
    };

    const handleCheckRecords = () => {
        if (!open) {
            navigate(`/complaint-check-list/${facilityId}`);
        }
    };


    const handleCardClick = (index) => {
        if (filledCardData[index]) {
            if (selectedCardId === index) {
                setSelectedCardId(null);
            } else {
                setSelectedCardId(index);
            }
        }
    };
    
    const userInputCheck = () => {
        return !selectedCardId && !inputValue.trim();
    };

    useEffect(() => {
        const noUserInput = userInputCheck();
        const buttonElement = document.getElementById("submitButton");

        if (buttonElement) {
            buttonElement.style.backgroundColor = noUserInput ?  'var(--sub-darkgrey-color)' : 'var(--main-blue-color)' ;
            buttonElement.disabled = noUserInput;
        }
    }, [selectedCardId, inputValue]);

    //민원 제출 처리
    const handleSubmit = async () => {
        setSummitLoading(true)
        const selectedComplaint = selectedCardId !== null ? filledCardData[selectedCardId] : '';
        const userInput = inputValue.trim();
        
        const complaintContent = [selectedComplaint, userInput]
            .filter(content => content) 
            .join(' ');

        if (!complaintContent) {
            setSnackbarOpen(true);
            return;
        }

        const complaintDatas = {
            complaintContent: complaintContent,
            facilityId: parseInt(facilityId, 10)
        };

        try {
            const response = await axios.post(`${common.getApiUrl()}/qr`, complaintDatas, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200) {
                navigate(`/complaint-input/${facilityId}?msg=${encodeURIComponent(complaintContent)}`);
            }
        } catch (error) {
            common.handleApiError(error);
            setSnackbarOpen(true);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // 카드 데이터를 3의 배수로 맞추기 위해 빈 카드 추가
    const filledCardData = [...complaintDatas];
    while (filledCardData.length % 3 !== 0) {
        filledCardData.push('');
    }

    // 최소 9개의 카드를 보장
    if (filledCardData.length < 9) {
        while (filledCardData.length < 9) {
            filledCardData.push('');
        }
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            backgroundColor: 'var(--main-white-color)',
        }}>
            {/* 헤더 */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '170px',
                backgroundColor: 'var(--main-blue-color)',
                color: '#fff'
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                }}>
                    <Typography variant="h6" sx={{ fontSize: '2.0rem', paddingTop:'30px', paddingX: '30px'  }}>
                        {facilityName}
                    </Typography>
                </Box>
                <Box sx={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    borderRadius: '15px',
                    padding: '4px 12px 4px 6px'
                }}
                    onClick={handleCheckRecords}
                >
                    <Typography sx={{ fontSize: '1.0rem', color: 'var(--main-blue-color)', paddingLeft:'10px' }}>점검기록</Typography>
                    <IconButton sx={{ color: 'var(--main-blue-color)', padding: 0, paddingLeft: '1px' }}>
                        <ArrowForwardIcon />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                width: '100%',
                padding: '10px 20px',
                color: '#696969',
                margin: '8px 0 8px 0'
            }}>
                <Typography sx={{ fontSize: '0.9rem' }}>
                    작성일시 : {new Date().toLocaleString('ko-KR', {
                    timeZone: 'Asia/Seoul',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                }).replace('.', '년 ').replace('.', '월 ').replace('.', '일 ').replace(/:.. /, ' ')}
                </Typography>
            </Box>
            <Box
                sx={{ margin: '20px', marginTop: '20px', bgcolor: '#fff', borderRadius: '15px' }}>
                <Box>
                    {complaintDatas.length > 0 ? (
                        <Typography sx={{ fontSize: '1.6rem', padding: '22px 0px 22px 40px' }} >
                            요청사항을 체크해주세요
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: '1.6rem', padding: '22px 0px 22px 40px' }} >
                            불편사항을 작성해주세요
                        </Typography>
                    )}
                </Box>
                {complaintDatas.length > 0 ? (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        padding: '10px 20px 20px 20px',
                        borderTop: '1px solid #3333'
                    }}>
                        {complaintDatas.map((data, index) => (
                            <Button
                                key={index}
                                onClick={() => handleCardClick(index)}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    backgroundColor: selectedCardId === index ? '#f0f0f0' : '#fff',
                                    padding: '10px 15px',
                                    borderRadius: '8px',
                                }}
                            >
                                {
                                    selectedCardId !== index ? (
                                        <img
                                            src={CheckOffIcon}
                                            alt="체크 해제"
                                            style={{
                                                marginRight: '25px',
                                                width: '29px',
                                                height: '29px'
                                            }}
                                        />
                                    ) : (
                                        <img
                                            src={CheckOnIcon}
                                            alt="체크 선택"
                                            style={{
                                                marginRight: '25px',
                                                width: '29px',
                                                height: '29px'
                                            }}
                                        />
                                    )
                                }
                                <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#333', opacity: selectedCardId !== index ? '70%' : '100%', }}>
                                    {data}
                                </Typography>
                            </Button>
                        ))}
                    </Box>
                ) : (null)}
            </Box>
            <Box
                sx={{
                    margin: '20px',
                    marginTop: '0',
                    bgcolor: '#fff',
                    borderRadius: '15px',
                    padding: '25px', // 상하 패딩 추가
                    display: 'flex', // Flexbox 사용
                    alignItems: 'center', // 수직 가운데 정렬
                    height: '70px', // 높이 명시적 설정 (필요에 따라 조정)
                }}
            >
                <BorderColor
                    sx={{
                        color: '#A9A9A9',
                        marginLeft: '10px', // 아이콘과 입력 필드 사이 간격 조정
                    }}
                />
                <Input
                    placeholder='불편사항을 작성해주세요'
                    value={inputValue}
                    autoComplete="off"
                    onChange={(e) => setInputValue(e.target.value)}
                    disableUnderline
                    sx={{
                        width: '100%',
                        paddingLeft: '30px',
                        fontSize: '18px', // 입력 텍스트 글자 크기
                        '&::placeholder': {
                            fontSize: '18px', // 플레이스홀더 글자 크기
                            color: '#A9A9A9', // 플레이스홀더 색상 (옵션)
                        },
                    }}
                />
            </Box>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    width: '100%',
                    py: 2,
                    backgroundColor: 'transparent',
                    display: 'flex',
                    justifyContent: 'center',
                    left: 0,
                    px: '20px',
                    boxSizing: 'border-box',
                    zIndex: 1300,
                    marginBottom: '2rem'
                }}
            >
                <Button
                    id="submitButton"
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{maxWidth: '220px', fontSize: '1.8rem', boxShadow: 'none', borderRadius: '50px',}}
                    disabled={summitLoading}
                    onClick={handleSubmit}
                >
                    접수하기
                </Button>
            </Box>
            <Dialog
                open={open}
                onClose={() => {
                }}
                PaperProps={{
                    style: {
                        backgroundColor: '#424242',
                        color: 'white',
                        borderRadius: '10px',
                        width: '300px',
                        maxWidth: '500px',
                        height: '100px',
                        marginTop: '-20vh',
                        boxShadow: 'none'
                    },
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none'
                    },
                }}
                disableEscapeKeyDown
                disableBackdropClick
            >
                <DialogContent>
                    <DialogContentText
                        sx={{ color: 'white', fontSize: '1.1rem', textAlign: 'center', boxShadow: 'none' }}>
                        접수 완료되었습니다.<br /> 페이지를 닫아주시기 바랍니다.
                    </DialogContentText>
                </DialogContent>
            </Dialog>
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
                    불편사항을 선택해주세요.
                </Alert>
            </Snackbar>
        </Box>
    );
}